// OutreachDetail — view + edit a single thread.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Chip from '../components/ui/Chip';
import type { OutreachStatus, OutreachThread } from '../lib/types';

const STORAGE_KEY = 'nice2job.outreach';

function loadThreads(): OutreachThread[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as OutreachThread[]) : [];
  } catch {
    return [];
  }
}
function saveThreads(threads: OutreachThread[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(threads));
}

const STATUSES: OutreachStatus[] = ['Draft', 'Sent', 'Replied', 'Bounced', 'Archived'];

export default function OutreachDetail() {
  const { threadId } = useParams();
  const nav = useNavigate();
  const [thread, setThread] = useState<OutreachThread | null>(null);

  useEffect(() => {
    const t = loadThreads().find((t) => t.id === threadId);
    setThread(t || null);
  }, [threadId]);

  if (!thread) {
    return (
      <div className="page-enter">
        <PageHeader eyebrow="Thread not found" title="We can't find this outreach." sub="Maybe it was archived." />
        <button className="btn" onClick={() => nav('/outreach')}>← Outreach</button>
      </div>
    );
  }

  const save = (patch: Partial<OutreachThread>) => {
    const next = { ...thread, ...patch };
    setThread(next);
    const all = loadThreads();
    const idx = all.findIndex((t) => t.id === thread.id);
    if (idx >= 0) all[idx] = next;
    else all.push(next);
    saveThreads(all);
  };

  const remove = () => {
    const all = loadThreads().filter((t) => t.id !== thread.id);
    saveThreads(all);
    nav('/outreach');
  };

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav('/outreach')}>← Outreach</button>
      <PageHeader
        eyebrow={`Thread · ${thread.to}`}
        title={thread.subject || 'Untitled thread'}
        sub={thread.role}
        actions={
          <>
            <select
              className="btn"
              value={thread.status}
              onChange={(e) => save({ status: e.target.value as OutreachStatus })}
              style={{ padding: '7px 12px' }}
            >
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <button className="btn ghost" onClick={remove}>Delete</button>
          </>
        }
      />
      <div className="card" style={{ padding: 26 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div className="avatar">{thread.to.split(' ').map((s) => s[0]).join('')}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{thread.to}</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{thread.role}</div>
          </div>
          <Chip>{thread.channel}</Chip>
        </div>
        <div className="tab" style={{ marginBottom: 6 }}>Subject</div>
        <input
          value={thread.subject}
          onChange={(e) => save({ subject: e.target.value })}
          style={inputStyle}
        />
        <div className="tab" style={{ margin: '18px 0 6px' }}>Body</div>
        <textarea
          value={thread.body || ''}
          onChange={(e) => save({ body: e.target.value })}
          rows={14}
          style={{ ...inputStyle, fontFamily: 'Fraunces, var(--cjk-serif), serif', lineHeight: 1.7, resize: 'vertical' }}
        />
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8,
  fontSize: 13.5, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)',
};
